import { zodResolver } from '@hookform/resolvers/zod';
import { browserSupportsWebAuthn, WebAuthnError } from '@simplewebauthn/browser';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import { QueryKey } from '@/Config/api/queryKey';
import i18n from '@/Config/app/i18n';
import type {
  Passkey,
  PasskeyDeleteInput,
  PasskeyRegisterInput,
} from '@/Domain/api/User/dto/Passkey.dto';
import { isPasskeyCeremonyCancelled } from '@/Domain/utils/passkeyErrors';

const PASSKEY_LIMIT_REACHED = 'PASSKEY_LIMIT_REACHED';
const PASSKEY_ALREADY_REGISTERED = 'PASSKEY_ALREADY_REGISTERED';
const PASSKEY_PASSWORD_REQUIRED = 'PASSKEY_PASSWORD_REQUIRED';
const PASSKEY_RECENT_LOGIN_REQUIRED = 'PASSKEY_RECENT_LOGIN_REQUIRED';
const INCORRECT_PASSWORD = 'incorrect_password';
const TOO_MANY_REQUESTS_STATUS = 429;
const PASSKEYS_QUERY_KEY = [QueryKey.user.passkeys];

const PasskeyPasswordValidator = z.object({
  current_password: z.string().min(1, i18n.t('validation:password_required')),
});

export type PasskeyPasswordForm = z.infer<typeof PasskeyPasswordValidator>;
type PasswordAction = { kind: 'register' } | { kind: 'delete'; passkeyId: number };

type ApiErrorBody = { errors?: Record<string, { message: string; code: string }[]> };

const errorCode = (error: unknown, field: string): string | undefined =>
  (error as AxiosError<ApiErrorBody>).response?.data?.errors?.[field]?.[0]?.code;

const registrationErrorKey = (error: unknown): string | null => {
  if (isPasskeyCeremonyCancelled(error)) {
    return null;
  }
  if (
    error instanceof WebAuthnError &&
    error.code === 'ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED'
  ) {
    return 'user_profile:passkeys.toast.already_registered';
  }
  if ((error as AxiosError).response?.status === TOO_MANY_REQUESTS_STATUS) {
    return 'ERRORS:TOO_MANY_REQUESTS';
  }
  const code = errorCode(error, 'root');
  if (code === PASSKEY_ALREADY_REGISTERED) {
    return 'user_profile:passkeys.toast.already_registered';
  }
  if (code === PASSKEY_LIMIT_REACHED) {
    return 'user_profile:passkeys.toast.limit_reached';
  }
  if (code === PASSKEY_RECENT_LOGIN_REQUIRED) {
    return 'user_profile:passkeys.toast.recent_login_required';
  }
  return 'user_profile:passkeys.toast.add_error';
};

export const usePasskeys = () => {
  const { t } = useTranslation(['user_profile', 'ERRORS']);
  const queryClient = useQueryClient();
  const [isSupported] = useState(() => browserSupportsWebAuthn());
  const [passwordAction, setPasswordAction] = useState<PasswordAction | null>(null);

  const passwordForm = useForm<PasskeyPasswordForm>({
    resolver: zodResolver(PasskeyPasswordValidator),
    defaultValues: { current_password: '' },
  });

  const {
    data: passkeys = [],
    isLoading,
    isError,
    refetch,
  } = useQuery<Passkey[]>({ queryKey: PASSKEYS_QUERY_KEY });

  // Cancel in-flight list reads so an older response can't overwrite the mutation, then reconcile with the server.
  const applyAndReconcile = async (update: (prev: Passkey[]) => Passkey[]): Promise<void> => {
    await queryClient.cancelQueries({ queryKey: PASSKEYS_QUERY_KEY });
    queryClient.setQueryData<Passkey[]>(PASSKEYS_QUERY_KEY, (prev) => update(prev ?? []));
    void queryClient.invalidateQueries({ queryKey: PASSKEYS_QUERY_KEY });
  };

  const closePasswordPrompt = () => {
    setPasswordAction(null);
    passwordForm.reset();
  };

  const handlePasswordError = (error: unknown, action: PasswordAction): boolean => {
    const code = errorCode(error, 'current_password');
    if (code !== PASSKEY_PASSWORD_REQUIRED && code !== INCORRECT_PASSWORD) {
      return false;
    }
    setPasswordAction(action);
    if (code === INCORRECT_PASSWORD) {
      passwordForm.setError('current_password', {
        message: t('user_profile:passkeys.password_incorrect'),
      });
    }
    return true;
  };

  const registerMutation = useMutation<Passkey, unknown, PasskeyRegisterInput>({
    mutationKey: [QueryKey.user.passkeyRegister],
    onSuccess: async (passkey) => {
      closePasswordPrompt();
      await applyAndReconcile((prev) => [passkey, ...prev.filter((p) => p.id !== passkey.id)]);
      toast.success(t('user_profile:passkeys.toast.added'));
    },
    onError: (error) => {
      if (handlePasswordError(error, { kind: 'register' })) {
        return;
      }
      const key = registrationErrorKey(error);
      if (key) {
        toast.error(t(key));
      }
    },
  });

  const deleteMutation = useMutation<void, unknown, PasskeyDeleteInput>({
    mutationKey: [QueryKey.user.passkeyDelete],
    onSuccess: async (_, { passkeyId }) => {
      closePasswordPrompt();
      await applyAndReconcile((prev) => prev.filter((passkey) => passkey.id !== passkeyId));
      toast.success(t('user_profile:passkeys.toast.deleted'));
    },
    onError: (error, { passkeyId }) => {
      if (handlePasswordError(error, { kind: 'delete', passkeyId })) {
        return;
      }
      const key =
        errorCode(error, 'root') === PASSKEY_RECENT_LOGIN_REQUIRED
          ? 'user_profile:passkeys.toast.delete_recent_login_required'
          : (error as AxiosError).response?.status === TOO_MANY_REQUESTS_STATUS
            ? 'ERRORS:TOO_MANY_REQUESTS'
            : 'user_profile:passkeys.toast.delete_error';
      toast.error(t(key));
    },
  });

  return {
    isSupported,
    passkeys,
    isLoading,
    isError,
    retry: () => refetch(),
    addPasskey: () => registerMutation.mutate({}),
    isAdding: registerMutation.isPending,
    needsPassword: passwordAction !== null,
    passwordDeleteName:
      passwordAction?.kind === 'delete'
        ? passkeys.find((passkey) => passkey.id === passwordAction.passkeyId)?.name
        : undefined,
    isVerifying: registerMutation.isPending || deleteMutation.isPending,
    passwordForm,
    submitPassword: passwordForm.handleSubmit((data) => {
      if (passwordAction?.kind === 'delete') {
        deleteMutation.mutate({
          passkeyId: passwordAction.passkeyId,
          currentPassword: data.current_password,
        });
      } else if (passwordAction?.kind === 'register') {
        registerMutation.mutate({ currentPassword: data.current_password });
      }
    }),
    cancelPassword: closePasswordPrompt,
    deletePasskey: (passkeyId: number) => deleteMutation.mutate({ passkeyId }),
    deletingId: deleteMutation.isPending ? deleteMutation.variables.passkeyId : null,
  };
};
